import 'array-flat-polyfill';

import {
  instantiateAPI_AC,
  setAuthToken_AC,
} from 'exacta-material-react/lib/src/store/actions/api';
import { authorizeWithToken_AC } from 'exacta-material-react/lib/src/store/actions/auth';
import QueryString from 'query-string';
import React, { Fragment, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
  withRouter,
} from 'react-router-dom';
import { filter } from 'rxjs/operators';

import { CssBaseline } from '@material-ui/core';

import LessonControlPanel from '../../components/LessonControlPanel';
import LoadingProgress from '../../components/LoadingProgess';
import { API_DOMAIN, API_PORT, CONTEXT_PATH } from '../../env/variables';
import {
  CommunicationMessageTypes,
  LessonControlMessage,
} from '../../helpers/constants';
import createLogger, { ILogger, LogPayload } from '../../helpers/logger';
import {
  composeFunctions,
  compressString,
  openDebugLogger,
  uint8ArrayToBase64,
} from '../../helpers/utils';
import useCommunicator from '../../hooks/useCommunicator';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import ActionService from '../../store/actions.service';
import { updateDialogMonitor_AC } from '../../store/actions/executor.actions';
import {
  IControlMessage,
  ILogControlMessage,
  isLessonControlMessage,
} from '../../types/communicator-types';
import { RootState } from '../../types/rootState';
import CatizTouchPanel from '../Catiz3/TouchPanel';
import CatizLauncher from '../CatizLauncher';
import DebugLogger from '../DebugLogger';
import Hermesys from '../Hermesys';
import AVA20 from '../Hermesys/ExComp/AVA20';
import GX4100 from '../Hermesys/ExComp/GX4100';
import M3SR from '../Hermesys/ExComp/M3SR';
import { HermsysMain } from '../Hermesys/main';
import { TCTWindowManager } from '../Hermesys/tct';
import LessonLauncher from '../LessonLauncher';
import Logout from '../Logout';
import LocalLauncher from '../Main/LocalLauncher';
import Desk from '../S80/components/Desk';
import LowerMonitor from '../S80/LowerMonitor';
import UpperMonitor from '../S80/UpperMonitor';
import TouchPanel from '../TouchPanel';
import appRoutes from './routes';

const Login = lazy(() => import('../Login'));
const Main = lazy(() => import('../Main'));
function NoMatch() {
  let location = useLocation();

  return (
    <div>
      <h3>
        No match for <code>{location.pathname}</code>
      </h3>
    </div>
  );
}

interface Props {
  history: any;
  location: Object;
}

const logger = createLogger('App/index.tsx');

const App: React.FunctionComponent<any> = (props) => {
  const { history } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const [loginState, setLoginState] = useState('unset');

  const { authenticated, token: storeToken } = useSelector(
    (state: RootState) => state.authenticate
  );
  // TODO temporary work around
  // const [isInstantiated, setIsInstantiated] = useState(true);
  const [isInstantiated, setIsInstantiated] = useState(false);
  const { t } = useTranslation();
  const { token = '', tct = 'false' } = QueryString.parse(location.search);
  const communicator = useCommunicator<IControlMessage>(
    CommunicationMessageTypes.ControlMessage
  );
  const logCommunicator = useCommunicator<ILogControlMessage>(
    CommunicationMessageTypes.LogControl
  );

  // run the hook on Ctrl+Shift+D
  useKeyboardShortcut(() => {
    openDebugLogger();
  }, ['Control', 'Shift', 'D']);

  useEffect(() => {
    if (location.pathname === appRoutes.DEBUG_LOGGER) {
      return;
    }
    logCommunicator?.receiveMessage((message) => {
      if (message.type === 'LogRequest') {
        console.log('sending logs', logger.getLogs());
        const payload = composeFunctions<
          ILogger,
          LogPayload,
          string,
          Uint8Array,
          string
        >(
          (_logger) => _logger.getLogs(),
          JSON.stringify,
          compressString,
          uint8ArrayToBase64
        )(logger);
        console.log('payload', payload);
        logCommunicator.postMessage({
          type: 'LogResponse',
          payload: payload,
        });
      }
    });

    return () => {
      logCommunicator?.close();
    };
  }, [logCommunicator]);
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'tryExacta') {
      dispatch(
        instantiateAPI_AC({
          domain: 'try.exacta-platform.com',
          port: '',
          isSecured: true,
        })
      );
      setIsInstantiated(true);
    } else {
      dispatch(
        instantiateAPI_AC({
          domain: API_DOMAIN,
          port: API_PORT,
          isSecured: window.location.protocol === 'https:',
          contextPath: CONTEXT_PATH,
        })
      );
      setIsInstantiated(true);
    }
  }, [history, dispatch]);

  useEffect(() => {
    communicator?.receiveMessage((msg) => {
      console.log('APP: got message ', msg);
      if (isLessonControlMessage(msg)) {
        if (
          msg.controlMessage === LessonControlMessage.LOGOUT_ON_EXIT &&
          !(window as any).logoutInitiator
        ) {
          window.close();
        }
        if (msg.controlMessage === LessonControlMessage.UPDATE_DIALOG_MONITOR) {
          dispatch(
            updateDialogMonitor_AC({
              monitor: msg.monitor,
            })
          );
        }
      }
    });
  }, [communicator]);

  useEffect(() => {
    window.onbeforeunload = () => {
      // Clear the localstorage we don't need residual lesson state
      // May be clear only the redux state? Should think about this.
      // TODO: evaluate alternate forms
      // localStorage.clear();
    };
  }, []);

  useEffect(() => {
    const subscription = ActionService.getActions()
      .pipe(filter((action: any) => action.type === 'API_LOGOUT'))
      .subscribe(() => {
        setLoginState('logged-out');
        history.push('/logout');
      });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (token && loginState === 'unset') {
      console.log('token notsplayer4', token);
      dispatch(authorizeWithToken_AC(token));
      dispatch(setAuthToken_AC(token));
      setLoginState('logged-in');
    }
  }, [token]);
  //1000 * 60 * (process.env.REACT_APP_ENV === 'development' ? 2 : 15)
  if (location.pathname === appRoutes.LAUNCHER) {
    return <LocalLauncher />;
  }
  return (
    <Fragment>
      <CssBaseline />
      {/* TODO temporary work around  */}
      {/* {isInstantiated ? ( */}
      {isInstantiated && authenticated && storeToken ? (
        <>
          <Switch>
            <Redirect exact from={appRoutes.HOME} to={appRoutes.MAIN} />
            <Route
              exact
              path={appRoutes.LOGIN}
              render={(props) => <Login {...props} />}
            />
            <Route
              exact
              path={appRoutes.HERMESYS_MAIN}
              render={(props) =>
                tct === 'true' ? (
                  <TCTWindowManager {...props} />
                ) : (
                  <HermsysMain {...props} />
                )
              }
            />
            {/* <Route
              exact
              path={appRoutes.HERMESYS_TCT}
              render={(props) => <TCTWindowManager {...props} />}
            /> */}
            <Route
              exact
              path={appRoutes.HERMESYS_M3SR}
              render={(props) => <M3SR {...props} />}
            />
            <Route
              exact
              path={appRoutes.HERMESYS_GX4100}
              render={(props) => <GX4100 {...props} />}
            />
            <Route
              exact
              path={appRoutes.HERMESYS_AVA20}
              render={(props) => <AVA20 {...props} />}
            />
            <Route
              exact
              path={appRoutes.LOGOUT}
              render={(props) => <Logout {...props} />}
            />
            <Route
              path={appRoutes.MAIN}
              render={(props) => <Main {...props} />}
            />
            <Route
              exact
              path={appRoutes.DEBUG_LOGGER}
              render={(props) => <DebugLogger {...props} />}
            />
            <Route
              exact
              path={appRoutes.TOUCH_PANEL}
              render={(props) => <TouchPanel />}
            />
            <Route
              path={appRoutes.LESSON_CONTROLLER}
              render={() => <LessonControlPanel />}
            />
            <Route
              path={appRoutes.LOWER_MONITOR}
              render={() => <LowerMonitor />}
            />
            <Route
              path={appRoutes.UPPER_MONITOR}
              exact
              render={() => <UpperMonitor />}
            />
            <Route path={appRoutes.DESK} render={() => <Desk />} />
            <Route path={appRoutes.Catiz} render={() => <CatizLauncher />} />
            <Route
              path={appRoutes.Catiz3}
              render={(props) => <LessonLauncher {...props} />}
            />
            <Route
              path={appRoutes.CatizTouchPanel}
              render={() => <CatizTouchPanel />}
            />
            <Route
              path={appRoutes.CatizTouchPanelWithLogin}
              render={() => <LessonLauncher {...props} />}
            />
            <Route
              path={appRoutes.S80}
              render={(props) => <LessonLauncher {...props} />}
            />
            <Route
              path={appRoutes.HERMESYS}
              render={(props) => <LessonLauncher {...props} />}
            />
            <Route path="*">
              <NoMatch />
            </Route>
            {/* <Route
            path="/catiz"
            exact
            render={() => (
              <Catiz/>
            )}
          /> */}
            <Route
              path={appRoutes.HERMESYS}
              render={(props) => <Hermesys lessonDef={{}} {...props} />}
            />
          </Switch>
        </>
      ) : (
        <>
          {loginState === 'logged-out' ? (
            <Logout />
          ) : (
            <LoadingProgress message={t('loadingProgress.login')} />
          )}
        </>
      )}
    </Fragment>
  );
};

export default withRouter(App);
